'use client';

/**
 * 登录轮播组件（2026-10-08；内容定稿 2026-10-09）：登录页左侧与登录弹窗左侧共用的图片轮播。
 * - 数据源：GET /api/banners?type=login_carousel（后台 BannersTab 可增/编辑/排序/启停，2026-10-08）
 *   后台未配置时回退内置默认 6 张（public/carousel/）——保证零配置环境可用
 * - 4.5s 自动切换，700ms 淡入淡出；底部渐变遮罩 + 标语/副文案 + 可点击指示点
 * - variant: page = 登录页全出血铺满（大字号）；dialog = 弹窗左栏（紧凑字号）
 * - 图片路径运行时拼 basePath（staging/prod 子路径部署），useEffect 设置避免 hydration 不匹配
 * - 内容红线（2026-10-09 用户定稿）：卖点必须与产品实际能力一致——Seedance 2.5 旗舰视频模型、
 *   火山引擎官方直连（Doubao/Seedance/Seedream 全系 Ark API）、年付立省 2 个月（约 83 折）+
 *   注册送 200 积分、即梦 2.0 多模态口播、可视化工作流、Seedream 5.0 图像；
 *   禁止恢复「AI 数字人/虚拟形象/声音克隆」等产品不具备的能力表述（carousel-2 已弃用）
 */

import { useEffect, useState } from 'react';
import { getBasePath } from '@/lib/utils';
import { bannersApi, type SiteBannerDto } from '@/lib/api';

/** 内置默认（后台无配置时的回退；后台配置后此列表不生效） */
const DEFAULT_SLIDES: { src: string; title: string; desc: string }[] = [
  { src: '/carousel/carousel-5.jpg', title: 'Seedance 2.5 · 最新旗舰视频模型', desc: '一句话生成电影级画面 · 多风格运镜 · 1080p 直出' },
  { src: '/carousel/carousel-6.jpg', title: '火山引擎官方直连', desc: 'Doubao · Seedance · Seedream 官方 API · 稳定低延迟 · 无中间商差价' },
  { src: '/carousel/carousel-1.jpg', title: '真人感口播视频，形象声音一步到位', desc: '上传参考图与音频 · 即梦 2.0 多模态 · 自动生成成片' },
  { src: '/carousel/carousel-3.jpg', title: '可视化工作流，复杂创作化繁为简', desc: '节点拖拽 · 多模型自动调度 · 一键批量成片' },
  { src: '/carousel/carousel-4.jpg', title: 'Seedream 5.0 · 光影质感触手可及', desc: '旗舰图像模型 · 最高 4K 分辨率 · 商用级品质' },
  { src: '/carousel/carousel-7.jpg', title: '年付立省 2 个月 · 新人领 200 积分', desc: '注册即享每日免费积分 · 套餐年付约 83 折 · 随时弹性升级' },
];

interface SlideItem {
  src: string;
  title: string;
  desc: string;
}

function toSlides(items: SiteBannerDto[], basePath: string): SlideItem[] {
  return items
    .filter((b) => !!b.imageUrl)
    .map((b) => ({
      src: /^https?:\/\//i.test(b.imageUrl) ? b.imageUrl : `${basePath}${b.imageUrl}`,
      title: b.title || b.subtitle || '',
      desc: b.title && b.subtitle ? b.subtitle : '',
    }));
}

interface LoginCarouselProps {
  variant?: 'page' | 'dialog';
}

export function LoginCarousel({ variant = 'page' }: LoginCarouselProps) {
  const [current, setCurrent] = useState(0);
  const [basePath, setBasePath] = useState('');
  const [slides, setSlides] = useState<SlideItem[]>([]);

  useEffect(() => {
    const bp = getBasePath();
    setBasePath(bp);
    let alive = true;
    // 后台配置优先；接口失败/空数据回退内置默认
    bannersApi.list('login_carousel').then((items) => {
      if (!alive) return;
      const mapped = toSlides(items || [], bp);
      if (mapped.length > 0) setSlides(mapped);
    });
    return () => {
      alive = false;
    };
  }, []);

  const list = slides.length > 0 ? slides : DEFAULT_SLIDES.map((s) => ({ ...s, src: s.src }));

  // 自动轮播
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % list.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [list.length]);

  // 切换数据集时收敛 current 越界
  useEffect(() => {
    if (current >= list.length) setCurrent(0);
  }, [list.length, current]);

  const isPage = variant === 'page';

  return (
    <div className={`relative h-full w-full overflow-hidden bg-[#171717] ${isPage ? '' : 'rounded-l-2xl'}`}>
      {/* 图片层（淡入淡出） */}
      {list.map((slide, i) => (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          key={`${slide.src}-${i}`}
          src={slide.src}
          alt={slide.title}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
            i === current ? 'opacity-100' : 'opacity-0'
          }`}
          draggable={false}
        />
      ))}

      {/* 底部渐变遮罩 */}
      <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
      {/* 顶部轻微压暗（保证深色 UI 融合） */}
      <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/30 to-transparent" />

      {/* 标语区 */}
      <div className={`absolute inset-x-0 bottom-0 ${isPage ? 'px-10 pb-10' : 'px-6 pb-6'}`}>
        <div className={isPage ? 'min-h-[86px]' : 'min-h-[56px]'}>
          <h2
            className={`font-bold text-white ${isPage ? 'text-2xl xl:text-3xl' : 'text-lg'}`}
            style={{ textShadow: '0 2px 12px rgba(0,0,0,0.6)' }}
          >
            {list[current]?.title}
          </h2>
          {list[current]?.desc && (
            <p className={`mt-2 text-slate-300 ${isPage ? 'text-sm xl:text-base' : 'text-xs'}`}>
              {list[current].desc}
            </p>
          )}
        </div>

        {/* 指示点 */}
        <div className={`mt-4 flex items-center gap-2 ${isPage ? '' : 'mt-3 gap-1.5'}`}>
          {list.map((slide, i) => (
            <button
              key={`${slide.src}-dot-${i}`}
              type="button"
              onClick={() => setCurrent(i)}
              aria-label={`切换到第 ${i + 1} 张`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === current
                  ? 'w-6 bg-white'
                  : 'w-1.5 bg-white/40 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
